import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { Patient } from '@models';

import { PatientElationLinkResponse, PatientService } from '../../core/services/patient.service';
import { NavigationService } from '../../core/services/navigation.service';
import { ChatService } from '../../core/services/chat.service';
import { MembershipService } from '../../core/services/membership.service';
import { NiceMembershipService } from '../../core/services/nice-membership.service';
import { AlertService } from '../../core/services/alert.service';
import LoaderWrapper from '../../react/ui/Loader/LoaderWrapper';
import PatientInfoSummaryWrapper from '../../react/ui/PatientInfoSummary/PatientInfoSummaryWrapper';
import PatientInfoContactWrapper from '../../react/ui/PatientInfoContact/PatientInfoContactWrapper';
import PatientInfoMembershipWrapper from '../../react/ui/PatientInfoMembership/PatientInfoMembershipWrapper';
import { UserService } from '../../core/services/user.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import ElationChartButtonWrapper from '../../react/ui/buttons/ElationChartButton/ElationChartButtonWrapper';
import { Patient as NewApiPatient } from '../../react/api/model';
import { parseAndFormatDuration } from '../../react/utils';
import { MembershipInformation } from '../../core/types/memberships';

@Component({
  selector: 'app-patient-layout',
  templateUrl: './patient-layout.component.html',
  styleUrls: ['./patient-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PatientLayoutComponent implements OnInit, OnDestroy {
  public opened: Observable<boolean>;
  public readonly loading: Observable<boolean>;
  public _loading = new BehaviorSubject<boolean>(false);

  public patient$: Observable<Patient>;
  public patientIdChange: Subscription;
  public membership$: Observable<MembershipInformation>;

  public currentPatientId: number;

  public unreadMessageCountFromPatient: Observable<number>;

  public displayAge: string | undefined;
  public displayElationChartButton: boolean = false;
  public elationChartLink: string | undefined;
  public capsuleEligibility$: Observable<boolean>;
  public capsuleEligibilityCheckInProgress = false;
  public capsuleDeliveryAvailable = false;

  protected readonly LoaderWrapper = LoaderWrapper;
  protected readonly ElationChartButtonWrapper = ElationChartButtonWrapper;
  protected readonly PatientInfoSummaryWrapper = PatientInfoSummaryWrapper;
  protected readonly PatientInfoContactWrapper = PatientInfoContactWrapper;
  protected readonly PatientInfoMembershipWrapper = PatientInfoMembershipWrapper;

  private destroyed: Subject<boolean> = new Subject<boolean>();

  constructor(
    public alertService: AlertService,
    public membershipService: MembershipService,
    public niceMembershipService: NiceMembershipService,
    public navigationService: NavigationService,
    protected patientService: PatientService,
    private chatService: ChatService,
    private userService: UserService,
    private router: Router
  ) {
    this.loading = this._loading.asObservable();
  }

  ngOnInit() {
    this.opened = this.navigationService.sidenavOpened;
    this.patientIdChange = this.patientService.selectedPatientId
      .pipe(
        takeUntil(this.destroyed),
        tap((patientId) => {
          if (!patientId || parseInt(patientId, 10) !== this.currentPatientId) {
            this._loading.next(true);
          }
        }),
        shareReplay(1)
      )
      .subscribe();

    this.patient$ = this.patientService.selectedPatient.pipe(
      takeUntil(this.destroyed),
      tap((patient) => {
        // Remove the button for the new patient until we can be sure we know where to link them to (if they can link).
        this.displayElationChartButton = false;
        this.elationChartLink = undefined;
        this.displayAge = undefined;
      }),
      tap((patient) => {
        if (patient) {
          this.patientService
            .getElationPatientLinkByPatientId(patient.id)
            .then((linkResponse: PatientElationLinkResponse) => {
              this.displayElationChartButton = linkResponse.isElationPatient;
              this.elationChartLink = linkResponse.link;
            })
            .catch((error) => {
              console.error(error);
            });

          this.patientService
            .getPatientFromNewApi(patient.id)
            .then((newApiPatient: NewApiPatient | null) => {
              if (newApiPatient) {
                this.displayAge = parseAndFormatDuration(newApiPatient.age);
              }

              this.currentPatientId = patient.id;
              this._loading.next(false);
            })
            .catch((error) => {
              console.error(error);
              this.currentPatientId = patient.id;
              this._loading.next(false);
            });
        }
      }),
      shareReplay(1)
    );

    this.capsuleEligibility$ = this.patient$.pipe(
      takeUntil(this.destroyed),
      tap(() => {
        this.capsuleEligibilityCheckInProgress = true;
      }),
      switchMap((patient) => {
        if (patient) {
          if (patient.user.address) {
            return this.membershipService.getCapsuleEligibility(patient.user.address.zip).pipe(
              map((v) => {
                this.capsuleDeliveryAvailable = v.isDeliverable;
                this.capsuleEligibilityCheckInProgress = false;
                return v.isDeliverable;
              })
            );
          } else if (patient.user.addresses && patient.user.addresses.length > 0) {
            return this.membershipService.getCapsuleEligibility(patient.user.addresses[0].zip).pipe(
              map((v) => {
                this.capsuleDeliveryAvailable = v.isDeliverable;
                this.capsuleEligibilityCheckInProgress = false;
                return v.isDeliverable;
              })
            );
          } else {
            this.capsuleDeliveryAvailable = false;
            this.capsuleEligibilityCheckInProgress = false;

            return of(false);
          }
        } else {
          return of(false);
        }
      }),
      shareReplay(1)
    );

    this.membership$ = this.patient$.pipe(
      takeUntil(this.destroyed),
      filter((patient) => !!patient),
      tap(() => {
        this.capsuleEligibilityCheckInProgress = true;
      }),
      switchMap((patient) => {
        return this.niceMembershipService.getMembership((patient.user as any).id);
      }),
      shareReplay(1)
    );

    this.unreadMessageCountFromPatient = this.patient$.pipe(
      takeUntil(this.destroyed),
      filter((patient) => !!patient),
      switchMap((patient) => {
        return this.chatService.unreadMessages.pipe(
          map((messages) => {
            const unreadMessagesFromPatient = messages.filter((message) => message.patientId === patient.id);
            return unreadMessagesFromPatient.length;
          })
        );
      }),
      shareReplay(1)
    );

    this.capsuleEligibility$.pipe(takeUntil(this.destroyed), shareReplay(1)).subscribe();
    this.membership$.pipe(takeUntil(this.destroyed), shareReplay(1)).subscribe();
  }

  ngOnDestroy() {
    this.destroyed.next(true);
  }

  public async navigateToDocumentsTable(patientId: number) {
    if (environment.featuresEnabled.documents) {
      const user = await this.userService.getPatientUser(patientId);
      if (user !== undefined && user !== null) {
        const patientUserId = user.userId;
        window.open(environment.niceAdminUrl + '/documents/' + patientUserId, '_self');
      } else {
        this.alertService.error('Error opening documents page');
      }
    } else {
      await this.router.navigate(['/', 'patients', 'documents', 'list', patientId]);
    }
  }

  protected readonly environment = environment;
}
